import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchInternalAggregatorValidationApi } from "../../../api/client";
import { aggregatorValidationKeys } from "./aggregatorValidationKeys";
import { type AggregatorValidation, toAggregatorValidation } from "./aggregatorValidationTypes";

/**
 * Shared queryOptions for a deal's Internal-tab Aggregator Validation (key +
 * queryFn). Backed by the generated
 * `WorkbenchInternalAggregatorValidationApi.getAggregatorValidation`, which
 * forwards to recap-service and returns the CAPS-vs-aggregator contract
 * comparison; non-aggregator deals come back 200 with an empty validations
 * list (never 404). Normalized at the boundary via {@link toAggregatorValidation}
 * so the render path stays free of optional-field handling. `staleTime`
 * matches the app-wide 30s convention.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The numeric approval id.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function aggregatorValidationQuery(approvalId: string) {
  return queryOptions({
    queryKey: aggregatorValidationKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<AggregatorValidation> => {
      const response = await workbenchInternalAggregatorValidationApi.getAggregatorValidation(approvalId, { signal });
      return toAggregatorValidation(response.data);
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the Internal-tab Aggregator Validation for a deal. Disabled (never
 * fires) until the approval id is present, so callers can render an
 * empty/loading state instead of issuing a doomed request.
 * @param {string} approvalId - The numeric approval id.
 * @returns {ReturnType<typeof useQuery<AggregatorValidation>>} The TanStack Query result.
 */
export function useAggregatorValidation(approvalId: string) {
  return useQuery(aggregatorValidationQuery(approvalId));
}
